import { ZODIAC_CLASH_INSIGHTS } from '../../data/zodiacClashInsights';
import { getPresentationLanguage, isEnglishPresentation, localizedValue } from '../../localization/presentationLanguage';

const clashText = (es, en) => localizedValue({ es, en });

const MAX_VOTES = 3;

const ZODIAC_EMOJI = {
  aries: '♈',
  tauro: '♉',
  geminis: '♊',
  cancer: '♋',
  leo: '♌',
  virgo: '♍',
  libra: '♎',
  escorpio: '♏',
  sagitario: '♐',
  capricornio: '♑',
  acuario: '♒',
  piscis: '♓',
};

const ZODIAC_TRAIT = {
  aries: 'Impulsivo',
  tauro: 'Constante',
  geminis: 'Versátil',
  cancer: 'Sensible',
  leo: 'Magnético',
  virgo: 'Analítico',
  libra: 'Encantador',
  escorpio: 'Intenso',
  sagitario: 'Aventurero',
  capricornio: 'Estratégico',
  acuario: 'Visionario',
  piscis: 'Intuitivo',
};
const ZODIAC_TRAIT_EN = { aries: 'Bold', tauro: 'Steady', geminis: 'Versatile', cancer: 'Sensitive', leo: 'Magnetic', virgo: 'Analytical', libra: 'Charming', escorpio: 'Intense', sagitario: 'Adventurous', capricornio: 'Strategic', acuario: 'Visionary', piscis: 'Intuitive' };
const SIGN_LABEL_EN = { aries: 'Aries', tauro: 'Taurus', geminis: 'Gemini', cancer: 'Cancer', leo: 'Leo', virgo: 'Virgo', libra: 'Libra', escorpio: 'Scorpio', sagitario: 'Sagittarius', capricornio: 'Capricorn', acuario: 'Aquarius', piscis: 'Pisces' };

const SIGN_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagitario: 'fire',
  tauro: 'earth',
  virgo: 'earth',
  capricornio: 'earth',
  geminis: 'air',
  libra: 'air',
  acuario: 'air',
  cancer: 'water',
  escorpio: 'water',
  piscis: 'water',
};

const ELEMENT_VISUAL = {
  fire: {
    label: 'Fuego',
    accent: '#FF7A3D',
    accentSoft: 'rgba(255,122,61,0.18)',
    border: 'rgba(255,145,84,0.72)',
    aura: 'rgba(255,106,52,0.34)',
    cardBg: 'linear-gradient(165deg, rgba(255,126,78,0.22) 0%, rgba(120,28,12,0.56) 42%, rgba(10,4,4,0.82) 100%)',
    glow: '0 0 42px rgba(255,110,54,0.32)',
    ring: 'rgba(255,180,92,0.34)',
    sheen: 'linear-gradient(135deg, rgba(255,232,160,0.24), rgba(255,255,255,0.02) 42%, rgba(255,90,40,0.08) 100%)',
  },
  earth: {
    label: 'Tierra',
    accent: '#C59668',
    accentSoft: 'rgba(197,150,104,0.18)',
    border: 'rgba(197,150,104,0.68)',
    aura: 'rgba(152,112,78,0.30)',
    cardBg: 'linear-gradient(165deg, rgba(182,140,96,0.20) 0%, rgba(64,38,22,0.58) 42%, rgba(10,8,6,0.82) 100%)',
    glow: '0 0 38px rgba(197,150,104,0.24)',
    ring: 'rgba(214,178,126,0.28)',
    sheen: 'linear-gradient(135deg, rgba(255,232,190,0.16), rgba(255,255,255,0.02) 46%, rgba(168,120,74,0.08) 100%)',
  },
  air: {
    label: 'Aire',
    accent: '#7CFFD4',
    accentSoft: 'rgba(124,255,212,0.18)',
    border: 'rgba(124,255,212,0.70)',
    aura: 'rgba(124,255,212,0.28)',
    cardBg: 'linear-gradient(165deg, rgba(122,255,214,0.18) 0%, rgba(18,78,56,0.52) 42%, rgba(4,10,8,0.82) 100%)',
    glow: '0 0 40px rgba(124,255,212,0.24)',
    ring: 'rgba(164,255,228,0.26)',
    sheen: 'linear-gradient(135deg, rgba(220,255,245,0.20), rgba(255,255,255,0.02) 44%, rgba(122,255,214,0.07) 100%)',
  },
  water: {
    label: 'Agua',
    accent: '#6D98FF',
    accentSoft: 'rgba(109,152,255,0.18)',
    border: 'rgba(124,164,255,0.70)',
    aura: 'rgba(109,152,255,0.30)',
    cardBg: 'linear-gradient(165deg, rgba(92,138,255,0.18) 0%, rgba(26,34,92,0.58) 42%, rgba(4,6,12,0.82) 100%)',
    glow: '0 0 40px rgba(109,152,255,0.26)',
    ring: 'rgba(160,186,255,0.26)',
    sheen: 'linear-gradient(135deg, rgba(226,235,255,0.18), rgba(255,255,255,0.02) 46%, rgba(92,138,255,0.08) 100%)',
  },
  cosmic: {
    label: 'Cósmico',
    accent: '#FFD700',
    accentSoft: 'rgba(255,215,0,0.16)',
    border: 'rgba(255,215,0,0.62)',
    aura: 'rgba(255,215,0,0.24)',
    cardBg: 'linear-gradient(165deg, rgba(255,215,0,0.16) 0%, rgba(75,0,130,0.38) 42%, rgba(0,0,0,0.76) 100%)',
    glow: '0 0 40px rgba(255,215,0,0.20)',
    ring: 'rgba(255,230,120,0.24)',
    sheen: 'linear-gradient(135deg, rgba(255,246,196,0.18), rgba(255,255,255,0.02) 46%, rgba(255,215,0,0.08) 100%)',
  },
};

function getSignElement(sign) {
  const key = String(sign || '').toLowerCase();
  return SIGN_ELEMENT[key] || 'cosmic';
}

function getElementVisual(sign) {
  const element = getSignElement(sign);
  const visual = ELEMENT_VISUAL[element] || ELEMENT_VISUAL.cosmic;
  const labels = { fire: clashText('Fuego', 'Fire'), earth: clashText('Tierra', 'Earth'), air: clashText('Aire', 'Air'), water: clashText('Agua', 'Water'), cosmic: clashText('Cósmico', 'Cosmic') };
  return { ...visual, label: labels[element] || labels.cosmic };
}

function buildClashContext(signA, signB) {
  const a = getSignElement(signA);
  const b = getSignElement(signB);
  const va = ELEMENT_VISUAL[a] || ELEMENT_VISUAL.cosmic;
  const vb = ELEMENT_VISUAL[b] || ELEMENT_VISUAL.cosmic;

  if (isEnglishPresentation()) {
    const labels = { fire: 'Fire', earth: 'Earth', air: 'Air', water: 'Water', cosmic: 'Cosmic' };
    if (a === b) return `${labels[a]} vs ${labels[b]} · two energies of the same element compete for dominance`;
    return `${labels[a]} vs ${labels[b]} · two zodiac energies face each other today`;
  }
  if (a === b) return `${va.label} vs ${vb.label} · dos energías del mismo elemento compiten por el dominio`;

  const combo = `${a}-${b}`;
  if (combo === 'fire-water' || combo === 'water-fire') return 'Fuego vs Agua · choque emocional entre impulso e intuición';
  if (combo === 'air-earth' || combo === 'earth-air') return 'Aire vs Tierra · visión, estrategia y control frente a constancia';
  if (combo === 'fire-air' || combo === 'air-fire') return 'Fuego vs Aire · ambición y carisma en una batalla expansiva';
  if (combo === 'water-earth' || combo === 'earth-water') return 'Agua vs Tierra · sensibilidad profunda contra firmeza silenciosa';
  if (combo === 'fire-earth' || combo === 'earth-fire') return 'Fuego vs Tierra · impulso ardiente frente a resistencia disciplinada';
  if (combo === 'water-air' || combo === 'air-water') return 'Agua vs Aire · emoción e intelecto intentando dominar la escena';

  return `${va.label} vs ${vb.label} · dos naturalezas zodiacales se enfrentan hoy`;
}

function formatCountdownToNextReset() {
  const now = new Date();
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  const diff = Math.max(0, next.getTime() - now.getTime());
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
}

function getSignEmoji(sign) {
  const key = String(sign || '').toLowerCase();
  return ZODIAC_EMOJI[key] || '✨';
}

function getSignTrait(sign) {
  const key = String(sign || '').toLowerCase();
  return (isEnglishPresentation() ? ZODIAC_TRAIT_EN : ZODIAC_TRAIT)[key] || clashText('Cósmico', 'Cosmic');
}

function getDominionStatusLabel({ pos, pct }) {
  // “Dominante/Influyente/Ascendente” para que el usuario lo entienda al toque
  if (pos === 1) return clashText('Dominante', 'Dominant');
  if (pos === 2) return clashText('Influyente', 'Influential');
  if (pos === 3) return clashText('Ascendente', 'Rising');
  if (pct > 0) return clashText('En juego', 'In play');
  return clashText('Sin votos', 'No votes');
}

function prettySignLabel(sign) {
  const s = String(sign || '').toLowerCase();
  if (!s) return '';
  return isEnglishPresentation() ? (SIGN_LABEL_EN[s] || s.charAt(0).toUpperCase() + s.slice(1)) : s.charAt(0).toUpperCase() + s.slice(1);
}

const CLASH_ARCHETYPE_RULES = [
  { key: 'intense', label: 'Intensidad', signs: ['escorpio', 'aries', 'leo'] },
  { key: 'strategic', label: 'Estrategia', signs: ['capricornio', 'virgo', 'escorpio'] },
  { key: 'emotional', label: 'Emoción', signs: ['cancer', 'piscis', 'libra'] },
  { key: 'magnetic', label: 'Magnetismo', signs: ['leo', 'escorpio', 'tauro'] },
  { key: 'mental', label: 'Mente', signs: ['geminis', 'acuario', 'virgo'] },
  { key: 'stable', label: 'Estabilidad', signs: ['tauro', 'capricornio', 'virgo'] },
  { key: 'visionary', label: 'Visión', signs: ['acuario', 'sagitario', 'geminis'] },
];


function getClashStreakState() {
  return getSessionJSON('zdx:clash_streak_state', {
    current: 0,
    best: 0,
    lastCompletedDayKey: null,
  });
}

function setClashStreakState(next) {
  setSessionJSON('zdx:clash_streak_state', next);
}



function getPredictionState(dayKey, clashId) {
  return getSessionJSON(`zdx:clash_prediction:${dayKey || 'day'}:${clashId || 'clash'}`, null);
}

function setPredictionState(dayKey, clashId, value) {
  setSessionJSON(`zdx:clash_prediction:${dayKey || 'day'}:${clashId || 'clash'}`, value);
}

function scoreArchetypesForSigns(signs) {
  const score = {};
  (signs || []).forEach((sign) => {
    const s = safeLower(sign);
    CLASH_ARCHETYPE_RULES.forEach((rule) => {
      if (rule.signs.includes(s)) {
        score[rule.key] = (score[rule.key] || 0) + 1;
      }
    });
  });
  return score;
}

function getTopArchetypesFromPicks(signs) {
  const score = scoreArchetypesForSigns(signs);
  const ranked = CLASH_ARCHETYPE_RULES
    .map((rule) => ({ key: rule.key, label: rule.label, score: Number(score[rule.key] || 0) }))
    .sort((a, b) => b.score - a.score);
  return ranked.filter((x) => x.score > 0).slice(0, 2);
}

// eslint-disable-next-line no-unused-vars
function buildDecisionProfileSummary(picks) {
  const arr = Array.isArray(picks) ? picks.filter(Boolean) : [];
  if (arr.length < 3) {
    return {
      title: 'Perfil en formación',
      subtitle: 'Jugá más duelos para descubrir tu estilo zodiacal.',
    };
  }

  const tops = getTopArchetypesFromPicks(arr);
  const primary = tops[0]?.label || 'Intuición';
  const secondary = tops[1]?.label || 'Carácter';

  return {
    title: `${primary} dominante`,
    subtitle: `Tus elecciones se inclinan hacia ${safeLower(primary)} y ${safeLower(secondary)}.`,
  };
}

function getPlayerProfileState() {
  return getSessionJSON('zdx:clash_profile_state', {
    totalVotes: 0,
    picks: [],
    correctPredictions: 0,
    totalPredictions: 0,
  });
}

function setPlayerProfileState(next) {
  setSessionJSON('zdx:clash_profile_state', next);
}

function registerVoteInProfile(chosenSign, { predictionChoice = null, winnerChoice = null } = {}) {
  const prev = getPlayerProfileState();
  const picks = [safeLower(chosenSign), ...(Array.isArray(prev.picks) ? prev.picks : [])]
    .filter(Boolean)
    .slice(0, 24);

  const totalVotes = Number(prev.totalVotes || 0) + 1;

  const hadPrediction = predictionChoice === 'A' || predictionChoice === 'B';
  const gotPredictionRight = hadPrediction && winnerChoice && predictionChoice === winnerChoice;

  const next = {
    totalVotes,
    picks,
    totalPredictions: Number(prev.totalPredictions || 0) + (hadPrediction ? 1 : 0),
    correctPredictions: Number(prev.correctPredictions || 0) + (gotPredictionRight ? 1 : 0),
  };

  setPlayerProfileState(next);
  return next;
}

function buildResultNarrative({
  signA,
  signB,
  pctA,
  pctB,
  winnerChoice,
  question,
} = {}) {
  const labelA = prettySignLabel(signA);
  const labelB = prettySignLabel(signB);

  const numericPctA = Number(pctA || 0);
  const numericPctB = Number(pctB || 0);

  if (isEnglishPresentation()) {
    if (!winnerChoice || numericPctA === numericPctB) return `An exceptionally close matchup between ${labelA} and ${labelB}. The community split its energy evenly.`;
    const winner = winnerChoice === 'A' ? labelA : labelB;
    const loser = winnerChoice === 'A' ? labelB : labelA;
    return `${winner} prevailed over ${loser}. The community associated ${winner} more strongly with the central quality in this matchup.`;
  }

  if (
    !winnerChoice ||
    numericPctA === numericPctB
  ) {
    return `Duelo extremadamente parejo entre ${labelA} y ${labelB}. La energía quedó dividida.`;
  }

  const winnerSign =
    winnerChoice === 'A' ? signA : signB;

  const loserSign =
    winnerChoice === 'A' ? signB : signA;

  const winnerLabel = prettySignLabel(winnerSign);
  const loserLabel = prettySignLabel(loserSign);

  const displayWinnerLabel =
    winnerLabel === 'Geminis' ? 'Géminis' : winnerLabel;

  const displayLoserLabel =
    loserLabel === 'Geminis' ? 'Géminis' : loserLabel;

  const normalizedWinner = String(winnerSign || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const normalizedQuestion = String(question || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\wñ¿? ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const axes = [
    {
      id: 'riesgo',
      keywords: [
        'arriesga',
        'garantias',
        'riesgo',
        'oportunidad inesperada',
        'gran salto',
      ],
      intro:
        'Este duelo mide quién avanza incluso cuando el escenario todavía no ofrece certezas.',
    },
    {
      id: 'impulso',
      keywords: [
        'antes de tener todo resuelto',
        'mueve antes',
        'reacciona primero',
        'en caliente',
      ],
      intro:
        'Este eje enfrenta la acción inmediata con la necesidad de analizar cada paso.',
    },
    {
      id: 'emocion',
      keywords: [
        'lo que siente',
        'desde lo que siente',
        'emocion',
        'intuicion',
        'comprenda',
      ],
      intro:
        'Este duelo explora cuánto pesa la emoción frente al análisis racional.',
    },
    {
      id: 'intensidad',
      keywords: [
        'mayor intensidad',
        'vive lo que siente',
        'profundidad',
        'disimularlo',
      ],
      intro:
        'Este eje compara la profundidad con la que cada signo experimenta y procesa sus emociones.',
    },
    {
      id: 'confianza',
      keywords: [
        'bajar la guardia',
        'confiar',
        'abrirse',
        'vulnerable',
        'sincero',
      ],
      intro:
        'Este duelo mide cuánto necesita cada signo para sentirse seguro antes de mostrarse vulnerable.',
    },
    {
      id: 'disciplina',
      keywords: [
        'disciplina cuando nadie lo observa',
        'mantiene la disciplina',
        'cuando nadie lo observa',
        'habitos incluso cuando nadie',
      ],
      intro:
        'Este duelo pone a prueba quién mantiene la disciplina incluso cuando nadie lo observa.',
    },
    {
      id: 'constancia',
      keywords: [
        'constante',
        'largo plazo',
        'persevera',
        'motivacion',
        'sostiene sus decisiones',
      ],
      intro:
        'Este eje mide quién mantiene una decisión cuando desaparece la motivación inicial.',
    },
    {
      id: 'conflicto',
      keywords: [
        'discusion',
        'provocando',
        'traicion',
        'problemas',
        'equivoco',
        'explota',
      ],
      intro:
        'Este duelo enfrenta dos formas distintas de reaccionar ante la presión y el conflicto.',
    },
    {
      id: 'independencia',
      keywords: [
        'necesita mas espacio propio',
        'espacio propio incluso en cercania',
        'necesita mas libertad',
        'libertad dentro de una pareja',
        'conservar su autonomia',
        'independencia',
      ],
      intro:
        'Este duelo pone a prueba cuánto espacio personal necesita cada signo incluso dentro de un vínculo cercano.',
    },
    {
      id: 'adaptacion',
      keywords: [
        'adapta',
        'plan deja de funcionar',
        'cambiar de opinion',
        'caos',
        'grupos de personas',
      ],
      intro:
        'Este eje mide quién modifica mejor su estrategia cuando el escenario cambia.',
    },
    {
      id: 'ambicion',
      keywords: [
        'meta',
        'resultado',
        'profesional',
        'liderar',
        'reconocimiento',
        'idea en algo concreto',
      ],
      intro:
        'Este duelo compara cómo transforma cada signo su ambición en decisiones concretas.',
    },
    {
      id: 'vinculos',
      keywords: [
        'relacion',
        'pareja',
        'amor',
        'enamorado',
        'vinculo',
        'persona que marco',
      ],
      intro:
        'Este eje explora cómo se comporta cada signo cuando un vínculo realmente le importa.',
    },
  ];

  const axis =
    axes.find((item) =>
      item.keywords.some((keyword) =>
        normalizedQuestion.includes(keyword)
      )
    ) || {
      id: 'general',
      intro:
        'La comunidad comparó qué signo representa mejor la cualidad central de este duelo.',
    };

  const reasons = {
    aries: {
      riesgo:
        'Aries rara vez espera el momento perfecto; prefiere descubrir el camino mientras avanza.',
      impulso:
        'Aries responde rápido, confía en su impulso y elige actuar antes que quedarse inmóvil.',
      conflicto:
        'Aries enfrenta la tensión de forma directa y difícilmente oculta su reacción inicial.',
      ambicion:
        'Aries convierte la competencia en impulso y necesita sentir que está avanzando.',
      disciplina:
        'Aries puede sostener una rutina cuando la convierte en un desafío personal y siente que está superándose.',
      independencia:
        'Aries necesita conservar iniciativa y margen de acción para no sentir que pierde autonomía dentro del vínculo.',
      general:
        'Aries suele destacar cuando la situación exige iniciativa, coraje y decisión inmediata.',
    },

    tauro: {
      disciplina:
        'Tauro mantiene hábitos con enorme firmeza cuando forman parte de una estructura estable y previsible.',
      constancia:
        'Tauro avanza a un ritmo estable y suele sostener lo que empieza incluso sin reconocimiento externo.',
      confianza:
        'Tauro necesita seguridad real antes de abrirse, pero cuando confía lo hace con enorme estabilidad.',
      vinculos:
        'Tauro valora los vínculos que ofrecen presencia, coherencia y continuidad en el tiempo.',
      independencia:
        'Tauro valora su espacio, pero suele preferir una cercanía estable antes que una libertad demasiado cambiante.',
      general:
        'Tauro representa firmeza, resistencia y una forma de avanzar sin abandonar fácilmente.',
    },

    geminis: {
      adaptacion:
        'Géminis cambia de enfoque con rapidez y encuentra nuevas opciones cuando el plan inicial deja de servir.',
      emocion:
        'Géminis procesa primero desde la mente y necesita comprender lo que ocurre antes de fijar una postura.',
      conflicto:
        'Géminis puede desarmar la tensión cambiando el enfoque, usando palabras o encontrando una salida inesperada.',
      disciplina:
        'Géminis sostiene mejor la disciplina cuando la rutina incluye variedad, estímulo mental y objetivos cambiantes.',
      independencia:
        'Géminis necesita libertad mental, movimiento y variedad para no sentirse limitado, incluso cuando existe conexión emocional.',
      general:
        'Géminis destaca cuando la situación exige agilidad mental, curiosidad y flexibilidad.',
    },

    cancer: {
      emocion:
        'Cáncer decide desde lo que siente y registra matices emocionales que otros pueden pasar por alto.',
      intensidad:
        'Cáncer vive sus emociones con profundidad y suele protegerlas detrás de una actitud cautelosa.',
      confianza:
        'Cáncer tarda en bajar la guardia porque necesita comprobar que el vínculo es emocionalmente seguro.',
      vinculos:
        'Cáncer se involucra profundamente cuando siente que puede construir pertenencia y protección.',
      disciplina:
        'Cáncer mantiene sus hábitos cuando los conecta con el cuidado propio, la familia o una seguridad emocional concreta.',
      independencia:
        'Cáncer puede necesitar refugio personal, aunque normalmente busca más contención y pertenencia que distancia.',
      general:
        'Cáncer representa sensibilidad, protección y una lectura intuitiva de los vínculos.',
    },

    leo: {
      riesgo:
        'Leo asume riesgos cuando siente que puede defender lo que desea y expresar plenamente quién es.',
      conflicto:
        'Leo protege su orgullo y responde con firmeza cuando siente que pierde reconocimiento.',
      ambicion:
        'Leo necesita sentir que su esfuerzo deja una huella y puede inspirar o liderar a otros.',
      disciplina:
        'Leo puede ser muy constante cuando siente orgullo por lo que construye, incluso sin reconocimiento inmediato.',
      independencia:
        'Leo necesita un espacio propio donde pueda expresarse sin sentir que debe reducir su identidad para sostener el vínculo.',
      general:
        'Leo destaca cuando el duelo premia presencia, seguridad y capacidad de liderazgo.',
    },

    virgo: {
      emocion:
        'Virgo necesita observar, ordenar y comprender el escenario antes de dejarse llevar por lo que siente.',
      disciplina:
        'Virgo destaca por sostener sus objetivos mediante hábitos, análisis y una constancia silenciosa, incluso sin supervisión externa.',
      constancia:
        'Virgo sostiene sus objetivos mediante hábitos, análisis y atención constante a los detalles.',
      adaptacion:
        'Virgo reorganiza el caos detectando primero qué parte del sistema dejó de funcionar.',
      riesgo:
        'Virgo suele asumir riesgos solo después de revisar variables, consecuencias y posibles errores.',
      independencia:
        'Virgo necesita momentos de orden y privacidad para procesar lo que vive sin interferencias externas.',
      general:
        'Virgo representa análisis, precisión y decisiones construidas sobre información concreta.',
    },

    libra: {
      emocion:
        'Libra intenta equilibrar lo que siente con el impacto que su decisión tendrá sobre los demás.',
      conflicto:
        'Libra mide sus palabras y busca una salida que no destruya innecesariamente el vínculo.',
      vinculos:
        'Libra necesita reciprocidad y suele evaluar cuánto equilibrio real existe entre ambas partes.',
      disciplina:
        'Libra mantiene mejor sus hábitos cuando encuentra equilibrio, armonía y una razón clara para sostenerlos.',
      independencia:
        'Libra disfruta la cercanía, pero necesita conservar equilibrio personal y decisiones propias dentro de la relación.',
      general:
        'Libra destaca cuando la situación exige equilibrio, lectura social y capacidad de negociación.',
    },

    escorpio: {
      intensidad:
        'Escorpio vive lo emocional con enorme intensidad, aunque no siempre lo revele de inmediato.',
      confianza:
        'Escorpio necesita construir confianza antes de exponerse porque no entrega su vulnerabilidad a cualquiera.',
      conflicto:
        'Escorpio observa, procesa y suele responder cuando siente que comprendió toda la situación.',
      vinculos:
        'Escorpio se involucra de manera profunda y difícilmente vive un vínculo importante con indiferencia.',
      disciplina:
        'Escorpio puede mantener una disciplina intensa y silenciosa cuando una meta despierta su compromiso profundo.',
      independencia:
        'Escorpio puede involucrarse profundamente, aunque necesita controlar cuándo y cómo comparte su mundo interno.',
      general:
        'Escorpio representa profundidad, resistencia emocional y una percepción muy aguda de lo oculto.',
    },

    sagitario: {
      riesgo:
        'Sagitario avanza movido por la posibilidad de descubrir algo nuevo, incluso sin garantías completas.',
      impulso:
        'Sagitario prefiere explorar antes que quedar atrapado buscando una certeza imposible.',
      adaptacion:
        'Sagitario cambia de dirección con facilidad cuando encuentra una experiencia más estimulante.',
      disciplina:
        'Sagitario sostiene la disciplina cuando la siente como una vía hacia mayor libertad y crecimiento personal.',
      independencia:
        'Sagitario necesita libertad, movimiento y experiencias propias para no sentir que el vínculo limita su crecimiento.',
      general:
        'Sagitario destaca cuando el duelo premia libertad, optimismo y disposición a probar algo distinto.',
    },

    capricornio: {
      disciplina:
        'Capricornio convierte la disciplina en una estructura diaria y avanza incluso cuando nadie observa su esfuerzo.',
      constancia:
        'Capricornio avanza mediante disciplina y visión de largo plazo, incluso sin validación externa.',
      riesgo:
        'Capricornio puede asumir riesgos, pero suele hacerlo después de medir costos, tiempos y consecuencias.',
      confianza:
        'Capricornio baja la guardia lentamente porque necesita comprobar que el vínculo tiene estabilidad real.',
      ambicion:
        'Capricornio convierte objetivos grandes en pasos concretos y medibles.',
      independencia:
        'Capricornio protege su tiempo, sus objetivos y su autonomía, incluso cuando mantiene un compromiso estable.',
      general:
        'Capricornio representa estrategia, autocontrol y capacidad para sostener objetivos a largo plazo.',
    },

    acuario: {
      emocion:
        'Acuario toma distancia para observar el panorama completo antes de involucrarse emocionalmente.',
      adaptacion:
        'Acuario rompe estructuras conocidas y encuentra soluciones que otros todavía no habían considerado.',
      confianza:
        'Acuario protege su independencia y necesita espacio mental antes de mostrar todo lo que siente.',
      disciplina:
        'Acuario mantiene sus hábitos cuando entiende su propósito y puede adaptarlos a su propia manera de pensar.',
      independencia:
        'Acuario necesita preservar independencia mental y espacio propio, aun dentro de vínculos afectivos muy cercanos.',
      general:
        'Acuario destaca cuando el duelo premia visión, originalidad y pensamiento independiente.',
    },

    piscis: {
      emocion:
        'Piscis confía en su intuición y suele percibir lo que ocurre antes de poder explicarlo racionalmente.',
      intensidad:
        'Piscis absorbe lo que siente con gran profundidad y empatía, incluso cuando intenta disimularlo.',
      confianza:
        'Piscis puede abrirse cuando percibe conexión emocional, pero se repliega si siente frialdad o distancia.',
      vinculos:
        'Piscis vive los vínculos desde la entrega emocional, la empatía y la conexión simbólica.',
      disciplina:
        'Piscis sostiene mejor la disciplina cuando conecta la rutina con una visión emocional, creativa o espiritual.',
      independencia:
        'Piscis busca conexión emocional profunda y puede ceder parte de su espacio cuando siente una unión significativa.',
      general:
        'Piscis representa intuición, sensibilidad y una conexión profunda con el mundo emocional.',
    },
  };

  const winnerReason =
    reasons[normalizedWinner]?.[axis.id] ||
    reasons[normalizedWinner]?.general ||
    `${displayWinnerLabel} representó mejor la cualidad central del duelo.`;

  const contrastTemplates = {
    riesgo:
      'Frente a {loser}, la comunidad percibió en {winner} una mayor disposición a avanzar aun sin garantías completas.',

    impulso:
      'Frente a {loser}, terminó pesando más la rapidez con la que {winner} transforma una intención en acción.',

    emocion:
      'Frente a {loser}, la forma de sentir y decidir de {winner} resultó más representativa para la comunidad.',

    intensidad:
      'Frente a {loser}, la profundidad emocional de {winner} inclinó la lectura del duelo.',

    confianza:
      'Frente a {loser}, la comunidad identificó en {winner} una barrera emocional más marcada antes de confiar.',

    disciplina:
      'Frente a {loser}, la comunidad valoró más la capacidad de {winner} para convertir la disciplina en una rutina constante, incluso cuando nadie la observa.',

    constancia:
      'Frente a {loser}, pesó más la capacidad de {winner} para perseverar sin depender del reconocimiento inmediato.',

    conflicto:
      'Frente a {loser}, la reacción de {winner} fue percibida como más representativa de la situación planteada.',

    independencia:
      'Frente a {loser}, pesó más la necesidad de {winner} de conservar libertad e independencia aun cuando existe una conexión afectiva profunda.',

    adaptacion:
      'Frente a {loser}, la comunidad valoró más la manera en que {winner} modifica su estrategia.',

    ambicion:
      'Frente a {loser}, la capacidad de {winner} para convertir intención en progreso terminó inclinando la balanza.',

    vinculos:
      'Frente a {loser}, la forma de vincularse de {winner} fue la que mejor encajó con la situación.',

    general:
      'Frente a {loser}, la comunidad sintió que {winner} encarnaba mejor la cualidad central del duelo.',
  };

  const contrast = (
    contrastTemplates[axis.id] ||
    contrastTemplates.general
  )
    .replace('{winner}', displayWinnerLabel)
    .replace('{loser}', displayLoserLabel);

  return `${axis.intro} ${winnerReason} ${contrast}`;
}




// =========================
// Insight engine (premium)
// - Determinístico por día+clash para que no “salte” distinto al recargar
// - Evita repetirse en el corto plazo
// - Intenta mapear por tono de la pregunta
// =========================
function safeLower(s) {
  return String(s || '').toLowerCase();
}

function inferInsightBucket(question) {
  const q = safeLower(question);
  if (!q) return null;

  // buckets típicos (si tu archivo de insights usa otros keys, igual hacemos fallback)
  if (/(seduc|conquist|atracci|fascin|sexual|tocarte|magnet)/i.test(q)) return 'seduccion';
  if (/(discusi|pelea|argument|debate|gana una|voz)/i.test(q)) return 'poder';
  if (/(mente|mirada|lee|intuici|psicol|callad|silencio)/i.test(q)) return 'intuicion';
  if (/(manipul|juego|domina|peligro|obsesion|ignoro)/i.test(q)) return 'mente';
  if (/(desaparec|fantasma|niebla|misterio)/i.test(q)) return 'misterio';

  return null;
}

function getInsightPoolsForSign(sign) {
  const key = safeLower(sign);
  const data = ZODIAC_CLASH_INSIGHTS && ZODIAC_CLASH_INSIGHTS[key];
  if (!data || typeof data !== 'object') return null;
  return data;
}

function pickFromArray(arr, seedNumber) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  // seedNumber opcional (si viene, hacemos determinístico)
  const idx = typeof seedNumber === 'number'
    ? Math.abs(seedNumber) % arr.length
    : Math.floor(Math.random() * arr.length);
  return arr[idx];
}

function hashString(str) {
  // hash simple y rápido, suficiente para indexar
  const s = String(str || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

function _cleanQuestionText(raw) {
  let q = String(raw || '').trim();
  if (!q) return '';

  q = q
    .replace(/[\u{1F1E6}-\u{1F1FF}]/gu, '') // flags
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  q = q.replace(/\s+([?!.,:;])/g, '$1');
  q = q.replace(/([¿¡])\s+/g, '$1');

  return q;
}

function _looksLikeNonsenseQuestion(q) {
  const s = _cleanQuestionText(q);
  if (!s) return true;

  const lower = safeLower(s);

  // Muy corta / placeholder
  if (s.length < 14) return true;
  if (/^\?+$/.test(s) || /^¿\?+$/.test(s)) return true;
  if (/\b(placeholder|lorem|ipsum)\b/i.test(s)) return true;

  // No parece pregunta real
  const hasQuestionWords = /\bqu[ií]en|qu[eé]|cu[aá]l|c[uú]ando|d[oó]nde|c[oó]mo|por qu[eé]\b/i.test(lower);
  const endsAsQuestion = /\?$/.test(s);
  if (!endsAsQuestion && !hasQuestionWords) return true;

  // Patrón malo del screenshot: pronombre sin referente
  if (/\b(terminarlas|terminarlo|hacerlo|hacerlas)\b/i.test(lower)) return true;

  // Muy genérica y sin comparación
  const hasCompare = /(más|mejor|peor|gana|vence|domina|resiste|aguanta|impacta|impone|lidera)/i.test(s);
  if (!hasCompare && s.length < 40) return true;

  return false;
}

function _fallbackQuestionPool() {
  if (isEnglishPresentation()) return [
    'Who stays in control when pressure rises?', 'Who detects a lie first with a single look?', 'Who remains disciplined when nobody is watching?',
    'Who uses silence most effectively?', 'Who comes back stronger after a setback?', 'Who reads the energy in a room most accurately?',
    'Who wins an argument without raising their voice?', 'Who stands by long-term decisions without recognition?',
    'Who commands respect through presence rather than words?', 'Who makes better decisions in critical moments?',
  ];
  return [
  '¿Quién mantiene mejor el control cuando la presión aumenta?',
  '¿Quién detecta primero una mentira con solo una mirada?',
  '¿Quién mantiene la disciplina cuando nadie lo observa?',
  '¿Quién domina el silencio y lo usa a su favor?',
  '¿Quién resurge más fuerte después de una caída?',
  '¿Quién lee mejor la energía de una habitación al entrar?',
  '¿Quién gana una discusión sin levantar la voz?',
  '¿Quién sostiene sus decisiones a largo plazo, incluso cuando nadie lo reconoce?',
  '¿Quién impone respeto con presencia, no con palabras?',
  '¿Quién toma decisiones más acertadas en momentos críticos?',
];
}

function _pickFallbackQuestion({ dayKey, clashId, signA, signB, avoid = [] } = {}) {
  const pool = _fallbackQuestionPool();
  const seed = hashString(`${dayKey || ''}|${clashId || ''}|${safeLower(signA)}|${safeLower(signB)}|fallback`);

  const avoidSet = new Set((avoid || []).map(_cleanQuestionText).filter(Boolean));

  for (let step = 0; step < Math.min(12, pool.length); step++) {
    const idx = Math.abs(seed + step * 17) % pool.length;
    const candidate = _cleanQuestionText(pool[idx]);
    if (candidate && !avoidSet.has(candidate)) return candidate;
  }

  return _cleanQuestionText(pool[Math.abs(seed) % pool.length]);
}

function _getRecentQuestionsSession() {
  return getSessionJSON('zdx:clash_question_recent', []);
}

function _pushRecentQuestionSession(q) {
  const cleaned = _cleanQuestionText(q);
  if (!cleaned) return;

  const prev = _getRecentQuestionsSession();
  const next = [cleaned, ...(Array.isArray(prev) ? prev : [])]
    .filter(Boolean)
    .slice(0, 12);

  setSessionJSON('zdx:clash_question_recent', next);
}

function _stableQuestionKey({ dayKey, clashId } = {}) {
  return `zdx:clash_question:${getPresentationLanguage()}:${dayKey || 'day'}:${clashId || 'clash'}`;
}

function pickClashQuestionPremium({ rawQuestion, dayKey, clashId, signA, signB } = {}) {
  const stableKey = _stableQuestionKey({ dayKey, clashId });
  const cached = getSessionJSON(stableKey, null);

  if (cached && typeof cached === 'string' && cached.trim()) {
    return _cleanQuestionText(cached);
  }

  const normalizedRawQuestion = isEnglishPresentation() ? '' : normalizeClashQuestion(rawQuestion);
  const cleaned = _cleanQuestionText(normalizedRawQuestion);
  const recent = _getRecentQuestionsSession();
  const recentClean = (Array.isArray(recent) ? recent : [])
    .map(_cleanQuestionText)
    .filter(Boolean);

  let finalQ = cleaned;

  if (
    !finalQ ||
    !isHighQualityClashQuestion(finalQ) ||
    _looksLikeNonsenseQuestion(finalQ) ||
    recentClean.includes(finalQ)
  ) {
    finalQ = _pickFallbackQuestion({
      dayKey,
      clashId,
      signA,
      signB,
      avoid: recentClean,
    });
  }

  setSessionJSON(stableKey, finalQ);
  _pushRecentQuestionSession(finalQ);

  return finalQ;
}

function getSessionJSON(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function setSessionJSON(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function pickInsightPremium({ sign, opponent, question, dayKey, clashId } = {}) {
  const s = safeLower(sign);
  if (!s) return null;

  if (isEnglishPresentation()) {
    const selected = prettySignLabel(s);
    const other = prettySignLabel(opponent);
    return `${selected} reflects the instinct you trusted${other ? ` when compared with ${other}` : ''}. Your choice reveals how you read character and energy under pressure. ✨`;
  }

  // 1) Determinístico por duelo (misma sesión) para que se sienta “curado”
  const stableKey = `zdx:clash_insight:${getPresentationLanguage()}:${dayKey || 'day'}:${clashId || 'clash'}:${s}`;
  const cached = getSessionJSON(stableKey, null);
  if (cached && typeof cached === 'string') return cached;

  // 2) Historial corto para evitar repetición
  const recentKey = 'zdx:clash_insight_recent';
  const recent = getSessionJSON(recentKey, []);

  const pools = getInsightPoolsForSign(s);
  if (!pools) return null;

  const bucket = inferInsightBucket(question);

  // armamos candidatos en orden de preferencia
  const candidates = [];
  if (bucket && Array.isArray(pools[bucket])) candidates.push(...pools[bucket]);

  // si existe un pool “general”/“default”/“all”, lo sumamos
  if (Array.isArray(pools.general)) candidates.push(...pools.general);
  if (Array.isArray(pools.default)) candidates.push(...pools.default);

  // fallback: todos los arrays del signo
  const all = Object.values(pools).flat().filter(Boolean);
  if (candidates.length === 0) candidates.push(...all);

  if (candidates.length === 0) return null;

  // filtramos los que aparecieron muy recientemente
  const filtered = candidates.filter(t => !recent.includes(t));

  // determinismo suave: día+clash+sign+opponent
  const seed = hashString(`${dayKey || ''}|${clashId || ''}|${s}|${safeLower(opponent)}|${question || ''}`);

  const chosen = pickFromArray(filtered.length ? filtered : candidates, seed);

  // guardamos
  if (chosen) {
    const nextRecent = [chosen, ...recent].slice(0, 10);
    setSessionJSON(recentKey, nextRecent);
    setSessionJSON(stableKey, chosen);
  }

  return chosen || null;
}

function mapTodayToDay(data) {
  if (!data || data.ok !== true) return null;

  const dayKey = data.dayKey || null;
  const playedCount = Number(data.playedCount ?? 0);
  const totalClashes = Number(data.totalClashes ?? 3);
  const currentStreak = Number(data.currentStreak ?? 0);
  const bestStreak = Number(data.bestStreak ?? 0);

  const clashes = Array.isArray(data.clashes) ? data.clashes : [];

  // Buscar el primer clash no votado
  const next = clashes.find(c => !c.picked) || null;

  if (next) {
    return {
      _id: dayKey || 'today',
      dayKey,
      timezone: data.timezone || null,
      myVotes: playedCount,
      totalClashes,
      currentStreak,
      bestStreak,
      profile: data.profile || null,
      clashId: next.clashId,
      signA: next.signA,
      signB: next.signB,
      question: next.question || null,
      votesA: Number(next.votesA ?? 0),
      votesB: Number(next.votesB ?? 0),
      pctA: Number(next.pctA ?? 0),
      pctB: Number(next.pctB ?? 0),
      picked: next.picked || null,
      results: null,
    };
  }

  // No hay más duelos (ya votó los 3)
  return {
    _id: dayKey || 'today',
    dayKey,
    timezone: data.timezone || null,
    myVotes: playedCount,
    totalClashes,
    currentStreak,
    bestStreak,
    profile: data.profile || null,
    clashId: null,
    signA: null,
    signB: null,
    votesA: 0,
    votesB: 0,
    results: [],
  };
}


const normalizeClashQuestion = (value) => {
  const raw = String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\?\s*\?/g, '?')
    .replace(/\?\s*([💬🔥❤️✨⚔️🌙🧠])\s*\?/g, '? $1')
    .trim();

  if (!raw) return '';

  let normalized = raw;

  const replacements = new Map([
    [
      '¿Quién sostiene sus decisiones a largo plazo, incluso cuando nadie lo reconoce?',
      '¿Quién sostiene sus decisiones a largo plazo, incluso cuando nadie lo reconoce?',
    ],
    [
      '¿Quién muestra lo que siente con menos filtro?',
      '¿Quién muestra lo que siente con menos filtros?',
    ],
    [
      '¿Quién mantiene mejor el control cuando la presión aumenta?',
      '¿Quién mantiene mejor el control cuando la presión aumenta?',
    ],
  ]);

  normalized = replacements.get(normalized) || normalized;

  if (!normalized.startsWith('¿')) {
    normalized = `¿${normalized.replace(/^\?+/, '')}`;
  }

  const emojiMatch = normalized.match(/\s([💬🔥❤️✨⚔️🌙🧠])$/u);

  if (emojiMatch) {
    const emoji = emojiMatch[1];
    const withoutEmoji = normalized
      .slice(0, -emoji.length)
      .trim()
      .replace(/\?+$/, '');

    normalized = `${withoutEmoji}? ${emoji}`;
  } else {
    normalized = `${normalized.replace(/\?+$/, '')}?`;
  }

  return normalized;
};

const isHighQualityClashQuestion = (value) => {
  const question = normalizeClashQuestion(value);
  if (!question) return false;

  const lower = question.toLowerCase();

  const weakPatterns = [
    'quién es mejor',
    'quién es peor',
    'quién es más tóxico',
    'quién ama más',
    'quién es más loco',
    'quién gana',
    'qué signo es',
  ];

  if (weakPatterns.some((pattern) => lower.includes(pattern))) {
    return false;
  }

  const words = question
    .replace(/[¿?💬🔥❤️✨⚔️🌙🧠]/gu, '')
    .trim()
    .split(/\s+/);

  return words.length >= 7 && words.length <= 22;
};

export {
  MAX_VOTES,
  ZODIAC_EMOJI,
  ZODIAC_TRAIT,
  SIGN_ELEMENT,
  ELEMENT_VISUAL,
  getSignElement,
  getElementVisual,
  buildClashContext,
  formatCountdownToNextReset,
  getSignEmoji,
  getSignTrait,
  getDominionStatusLabel,
  prettySignLabel,
  getClashStreakState,
  setClashStreakState,
  getPredictionState,
  setPredictionState,
  getPlayerProfileState,
  setPlayerProfileState,
  registerVoteInProfile,
  buildResultNarrative,
  safeLower,
  pickClashQuestionPremium,
  pickInsightPremium,
  mapTodayToDay,
};
